import axios, { AxiosError, AxiosInstance, AxiosRequestHeaders } from 'axios'
import axiosRetry, { exponentialDelay } from 'axios-retry'
import { v4 as uuidv4 } from 'uuid'
import { Store } from 'redux'
import type { CheckoutAppState } from '.'
import { authTokenSelector, authenticationSelector, fingerprintHeaderValue, profilingIdSelector, tokenSelector } from './selectors'
import { setSessionExpired } from './consumer/reducer'
import { Route } from '../config/routes'
import { api } from '../env'
import { store as baseStore } from './store'
import { HttpService } from '@afterpaytouch/portal-api/api'
import { HttpService as TopazHttpService } from '@afterpaytouch/topaz-api/api'
import { RulesHttpService } from '@afterpaytouch/rules-service-api/api'
import { OrderStatusHttpService } from '@afterpaytouch/order-status-api/api'
import { isAxiosError, handleKasadaBlockResponseError } from '@afterpaytouch/portal-api/utils'
import { VerifyServiceApi, createVerifyServiceAxios } from '@afterpay/verify-service-api'
import { setAuthToken } from './auth/user/reducer'
import { hasOptedOutRememberMe } from '../flow/remember-me/optOut'
import { AUTH_SESSION_HEADER, PERSONALIZATION_POST_CAPTURE_TIMEOUT, TrackingEvent } from '../model'
import { logCustomMonitoringEvent } from '../integrations/monitoring'
import { reportJourneyNetworkError } from '../integrations/userJourney/networkSignals'

const WHITELIST_PATH = ['/portal/consumers/persistent-login/resume', '/portal/consumers/persistent-login/revoke', '/portal/consumers/notification']

const CommonHeaders = {
  'X-Requested-With': 'client:checkout-redesign-v1',
  'X-Auth-Token': authTokenSelector,
  'X-Device-Fingerprint': fingerprintHeaderValue,
  'X-Profiling-Session-Id': profilingIdSelector,
  'X-Transaction-Token': tokenSelector,
} as const

type CommonHeadersKeys = keyof typeof CommonHeaders

const BlacklistedFromLoggingHeaders: string[] = ['X-Auth-Token', AUTH_SESSION_HEADER]

export const getPortalAxios = (store: Store<CheckoutAppState>): AxiosInstance => {
  const instance: AxiosInstance = axios.create({
    baseURL: api.PORTAL_API_BASE_URL,
    withCredentials: true,
  })
  const resumePersistentLogin = async (): Promise<boolean> => {
    try {
      await instance.post('/portal/consumers/persistent-login/resume', undefined, {
        validateStatus: (status) => status === 201,
      })
      return true
    } catch (e) {
      return false
    }
  }
  instance.interceptors.request.use(async (config) => {
    config.headers = config.headers ?? {}

    let amplitudeSessionId
    try {
      amplitudeSessionId = (await import('amplitude-js')).default.getInstance()?.getSessionId()
    } catch (e) {
      console.warn('amplitude failed to load dynamically', e)
    }

    const state = store.getState()
    for (const [key, value] of Object.entries(CommonHeaders)) {
      const resolved = typeof value === 'function' ? value(state) : value
      if (resolved !== undefined) {
        config.headers[key] = resolved
      }
    }

    if (amplitudeSessionId !== undefined) {
      config.headers['x-amplitude-session-id'] = amplitudeSessionId
    }
    config.headers['X-Speleo-Trace-Id'] = `WEB-${uuidv4()}`
    return config
  })

  instance.interceptors.response.use(
    async (response) => response,
    handleKasadaBlockResponseError((error: AxiosError) => {
      logCustomMonitoringEvent(TrackingEvent.KASADA_BLOCK, {
        baseURL: error.config?.baseURL,
        url: error.config?.url,
      })
    })
  )

  instance.interceptors.response.use(
    async (response) => {
      const authToken: string | undefined = response?.headers?.['x-auth-token'] ?? undefined
      if (authToken !== undefined && authToken !== authTokenSelector(store.getState())) {
        store.dispatch(setAuthToken(authToken))
      }
      return response
    },
    async (error) => {
      reportJourneyNetworkError(error)
      if (isAxiosError(error)) {
        const retryCount = error.config?.['axios-retry']?.retryCount ?? 0
        const requestHeaders = error.config?.headers as AxiosRequestHeaders & Partial<typeof CommonHeaders>

        const sanitizedHeaders = Object.fromEntries(
          Object.entries(requestHeaders ?? {}).map(([key, value]) =>
            BlacklistedFromLoggingHeaders.includes(key as CommonHeadersKeys) ? [key, 'REDACTED'] : [key, value]
          )
        )

        if (error.message === 'Network Error' && retryCount === 0) {
          logCustomMonitoringEvent(
            TrackingEvent.AXIOS_NETWORK_ERROR,
            {
              url: error.config?.url,
              message: error.message,
              method: error.config?.method,
              headers: sanitizedHeaders,
              withCredentials: error.config?.withCredentials,
              page: window.location.href,
              origin: window.location.origin,
              userAgent: navigator.userAgent,
              responseStatus: error.response?.status,
              responseHeaders: error.response?.headers ?? null,
              timestamp: new Date().toISOString(),
            },
            'ERROR'
          )
        }

        if (error.response?.status === 401 && !WHITELIST_PATH.includes(error.config?.url ?? '')) {
          const { persistent } = authenticationSelector(store.getState())
          if (persistent && !hasOptedOutRememberMe() && (await resumePersistentLogin())) {
            return await instance.request(error.config ?? {})
          } else {
            await store.dispatch(setSessionExpired(error.config?.url))

            try {
              const client = await import('next/client')

              await client.router.push(Route.ENTER_EMAIL)
            } catch (e) {
              console.warn('failed to dynamically import next client', e)
              throw error
            }
          }
        }
      }
      throw error
    }
  )
  axiosRetry(instance, { retries: 3, retryDelay: exponentialDelay })
  return instance
}

export const portalAxios = new HttpService(getPortalAxios(baseStore))

const topazAxios: AxiosInstance = axios.create({
  baseURL: api.TOPAZ_BASE_URL,
  withCredentials: process.env.NODE_ENV === 'development',
})

const paymentsSourceCaptureAxios: AxiosInstance = axios.create({
  baseURL: api.PAYMENT_SOURCE_BASE_URL,
})

const orderStatusAxios: AxiosInstance = axios.create({
  baseURL: api.EVENT_ORDER_STATUS_BASE_URL,
  timeout: PERSONALIZATION_POST_CAPTURE_TIMEOUT,
})

const rulesServiceAxios: AxiosInstance = axios.create({
  baseURL: api.RULES_SERVICE_BASE_URL,
})

export const VERIFY_SERVICE_BASE_URL = api.VERIFY_SERVICE_BASE_URL
const verifyServiceAxios = createVerifyServiceAxios({
  baseURL: VERIFY_SERVICE_BASE_URL,
  store: baseStore,
  headerSelectors: {
    'X-Device-Fingerprint': fingerprintHeaderValue,
    'X-Profiling-Session-Id': profilingIdSelector,
    'X-Transaction-Token': tokenSelector,
  },
  clientName: 'checkout',
})

const authSessionsAxios = createVerifyServiceAxios({
  baseURL: api.PORTAL_API_BASE_URL,
  store: baseStore,
  headerSelectors: {
    'X-Device-Fingerprint': fingerprintHeaderValue,
    'X-Profiling-Session-Id': profilingIdSelector,
  },
  clientName: 'checkout-redesign-v1',
})

const genericApiServiceAxios: AxiosInstance = axios.create({})

export const createPaymentSourceCaptureApi = (): HttpService => new HttpService(paymentsSourceCaptureAxios)
export const createPortalApi = (): HttpService => portalAxios
export const createTopazApi = (): TopazHttpService => new TopazHttpService(topazAxios)
export const createOrderStatusApi = (): OrderStatusHttpService => new OrderStatusHttpService(orderStatusAxios)
export const createRulesServiceApi = (): RulesHttpService => new RulesHttpService(rulesServiceAxios)
export const createVerifyServiceApi = (): HttpService => new HttpService(verifyServiceAxios)
export const createVerifyServiceClient = (): VerifyServiceApi => new VerifyServiceApi(verifyServiceAxios)
export const createAuthSessionsApi = (): HttpService => new HttpService(authSessionsAxios)
export const createAppleGatewayApi = (): HttpService => portalAxios
export const createProxyServiceApi = (): HttpService => new HttpService(genericApiServiceAxios)
